import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";

interface MilestoneDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: number;
  projectCurrency?: string;
}

export function MilestoneDialog({ 
  open, 
  onOpenChange, 
  projectId,
  projectCurrency = 'USD'
}: MilestoneDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    
    const description = formData.get('description') as string;
    const amount = parseFloat(formData.get('amount') as string);
    const dueDateStr = formData.get('dueDate') as string;
    
    if (!description || !amount || !dueDateStr) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please fill in all fields",
      });
      return;
    }

    const { error } = await supabase
      .from('milestones')
      .insert({
        project_id: projectId,
        description,
        amount,
        currency: projectCurrency,
        due_date: dueDateStr,
        status: 'pending'
      });

    if (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to create milestone",
      });
      return;
    }

    toast({
      title: "Success",
      description: "Milestone created successfully",
    });
    
    queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Add Milestone</DialogTitle>
            <DialogDescription>
              Create a new milestone for this project.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                name="description"
                placeholder="Milestone description"
                className="col-span-3"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="amount">Amount ({projectCurrency})</Label>
              <Input
                id="amount"
                name="amount"
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                className="col-span-3"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="dueDate">Due Date</Label>
              <Input
                id="dueDate"
                name="dueDate"
                type="date"
                className="col-span-3"
                min={format(new Date(), 'yyyy-MM-dd')}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="submit">Create Milestone</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}